import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import ReactPlayer from 'react-player';
import { ActionButton } from '../buttons';
import { VideoPlayerOverlay } from './videoPlayerOverlay/VideoPlayerOverlay';
import { VideoPlayerPopup } from './videoPlayerPopup/VideoPlayerPopup';

import './styles.less';

export const VideoPlayer = ( props ) => {
    const [ videoController, setVideoController ] = useState( null );
    const [ isPlaying, setPlaying ] = useState( props.autoPlay );
    const [ isReady, setReady ] = useState( false );
    const [ duration, setDuration ] = useState( 0 );
    const [ showingResumePopup, showResumePopup ] = useState( false );

    /**
     * Hides the video popup if there is one being shown.
     */
    const hidePopup = useCallback( () => {
        if ( showingResumePopup ) {
            showResumePopup( false );
        }
    }, [ showingResumePopup, showResumePopup ] );

    /**
     * Controls the video and restarts it for the user.
     */
    const restart = useCallback( ( playAfter = false ) => {
        videoController.seekTo( 0, 'fraction' );
        if ( playAfter ) {
            setPlaying( true );
        }
    }, [ videoController, setPlaying ] );

    /**
     * Controls the video and seeks to a specified location in the video. A time
     * or percentage can be provided to seek to. It is expected that only one of
     * these properties (`percentage` or `time`) will be provided in the arguments,
     * but if both are provided for some reason, the time value is prioritized.
     */
    const seekTo = useCallback( ( { time, percentage } ) => {
        if ( !time && !percentage ) { return; }

        // Seek to a location in the video.
        videoController.seekTo(
            ( time )
                ? Math.min( Math.max( time, 0 ), duration ) // Time (prioritized)
                : Math.min( Math.max( percentage, 0 ), 1 ), // Percentage
            ( time )
                ? 'seconds' // Time (prioritized)
                : 'fraction', // Percentage
        );
    }, [ videoController, duration ] );

    /**
     * Gets video details about where the viewer is currently at in the video.
     * This includes the video's current time, full duration, and the percentage
     * through the video the viewer is.
     */
    const getViewingDetails = useCallback( () => {
        const videoTime = videoController ? videoController.getCurrentTime() : 0;
        const videoDuration = duration ? duration : 0;
        const videoPercentage = ( videoDuration !== 0 ) ? ( videoTime / videoDuration ) : 0;
        
        return { time: videoTime, duration: videoDuration, percentage: videoPercentage };
    }, [ duration, videoController ] );

    /**
     * Executes the provided callback providing the videos current state as
     * arguments, which includes the videos current time, full duration, and
     * percentage.
     */
    const executeCallback = useCallback( ( callback, overridePercentage = undefined ) => {
        if ( callback ) {
            if ( overridePercentage === undefined ) {
                const viewingDetails = getViewingDetails();
                callback( viewingDetails.time, viewingDetails.duration, viewingDetails.percentage );

            } else {
                callback(
                    duration ? ( duration * overridePercentage ) : 0, // Time
                    duration ? duration : 0, // Duration
                    overridePercentage, // Percentage
                );
            }
        }
    }, [ getViewingDetails, duration ] );

    useEffect( () => {
        // If the video is ready and the duration of the video has been grabbed,
        // potentially skip or allow the user to resume at their last place.
        if ( isReady && Boolean( duration ) ) {
            const skipToTime = props?.skipTo?.time;
            const skipToPercentage = props?.skipTo?.percentage;
            const resumableAtTime = props?.resumableAt?.time;
            const resumableAtPercentage = props?.resumableAt?.percentage;

            // If a time or percentage to skip to has been set in the props, seek to that
            // time or percentage using the video controller.
            if ( Boolean( skipToTime ) || Boolean( skipToPercentage ) ) {
                seekTo( { time: skipToTime, percentage: skipToPercentage } );
            }
            // Else if the there is resumable time set, give the user the option to go back
            // to that point.
            else if ( Boolean( resumableAtTime ) || Boolean( resumableAtPercentage ) ) {
                seekTo( { time: resumableAtTime, percentage: resumableAtPercentage } );
                showResumePopup( false );
            }
        }
    }, [ props.skipTo, props.resumableAt, isReady, duration, seekTo, showResumePopup ] );

    return (
        <div className="video-player-container">
            { /* TODO: Generalize the resume popup more. */ }
            { showingResumePopup &&
                <VideoPlayerOverlay>
                    <VideoPlayerPopup>
                        <VideoPlayerPopup.Header>
                            Welcome back
                        </VideoPlayerPopup.Header>

                        <VideoPlayerPopup.Body>
                            Would you like to pick up where you left off?
                        </VideoPlayerPopup.Body>

                        <VideoPlayerPopup.Footer>
                            <ActionButton important onClick={ () => {
                                hidePopup();
                                setPlaying( true );
                            } }>
                                Resume
                            </ActionButton>
                            <ActionButton onClick={ () => {
                                hidePopup();
                                restart( true );
                            } }>
                                Restart
                            </ActionButton>
                        </VideoPlayerPopup.Footer>
                    </VideoPlayerPopup>
                </VideoPlayerOverlay>
            }

            <ReactPlayer
                className="video-player"
                url={ props.url }
                width="100%"
                height="100%"

                onDuration={ setDuration }
                ref={ ( controller ) => {
                    setVideoController( controller );
                    if ( props.getController ) {
                        props.getController( controller );
                    }
                } }

                controls={ props.controls }
                light={ props.previewFirst }
                playing={ ( props.playing !== undefined ) ? props.playing : isPlaying && !showingResumePopup }
                volume={ props.volume }
                muted={ props.muted }

                onReady={ () => { executeCallback( props.onReady ); setReady( true ); } }
                onPlay={ () => executeCallback( props.onPlay ) }
                onPause={ () => executeCallback( props.onPause ) }
                onSeek={ () => executeCallback( props.onSeek ) }
                onEnded={ () => executeCallback( props.onComplete, 1 ) }
                onBuffer={ () => executeCallback( props.onBuffer ) }
                onBufferEnd={ () => executeCallback( props.onBufferEnd ) }
                onProgress={ () => executeCallback( props.onUpdate ) }
                progressInterval={ props.updateInterval }
            />
        </div>
    );
};

VideoPlayer.defaultProps = {
    getController: undefined,
    url: undefined,
    playing: undefined,
    volume: undefined,
    muted: undefined,
    autoPlay: false,
    controls: true,
    previewFirst: false,
    updateInterval: 2000,
    onUpdate: undefined,
    onPlay: undefined,
    onPause: undefined,
    onReady: undefined,
    onComplete: undefined,
    onSeek: undefined,
    onBuffer: undefined,
    onBufferEnd: undefined,

    skipTo: undefined,
    resumableAt: undefined,
};

VideoPlayer.propTypes = {
    getController: PropTypes.func,
    url: PropTypes.string,
    playing: PropTypes.bool,
    volume: PropTypes.number,
    muted: PropTypes.bool,
    autoPlay: PropTypes.bool,
    controls: PropTypes.bool,
    previewFirst: PropTypes.bool,
    updateInterval: PropTypes.number,
    onUpdate: PropTypes.func,
    onPlay: PropTypes.func,
    onPause: PropTypes.func,
    onReady: PropTypes.func,
    onComplete: PropTypes.func,
    onSeek: PropTypes.func,
    onBuffer: PropTypes.func,
    onBufferEnd: PropTypes.func,

    skipTo: PropTypes.shape( {
        percentage: PropTypes.number,
        time: PropTypes.number,
    } ),
    resumableAt: PropTypes.shape( {
        percentage: PropTypes.number,
        time: PropTypes.number,
    } ),
};
